import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { ModalShell } from '../../ui/ModalShell/ModalShell';
import { Radio } from '../../ui/Radio/Radio';
import { TextArea } from '../../ui/TextArea/TextArea';
import { DateInput } from '../../components/Forms/DateInput';
import { ValidationMessage } from '../../components/Forms/ValidationMessage';
import { Button } from '../../ui/Button/Button';
import type { SnoozeDuration } from '../../services/tasksService';

export interface IgnoreRelanceSubmission {
    snoozeDuration: SnoozeDuration;
    customDate: string | null;
    note: string | null;
}

interface IgnoreRelanceModalProps {
    taskId: string;
    customerName: string;
    formattedAmount: string | null;
    /** Pre-fills the note, so snoozing again does not blank the one already left. */
    initialNote?: string | null;
    onConfirm: (submission: IgnoreRelanceSubmission) => void | Promise<void>;
    onCancel: () => void;
}

function isFutureDate(value: string): boolean {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.getTime() > Date.now();
}

export const IgnoreRelanceModal: React.FC<IgnoreRelanceModalProps> = ({
    taskId,
    customerName,
    formattedAmount,
    initialNote = null,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation();
    const [duration, setDuration] = useState<SnoozeDuration>('Hours24');
    const [customDate, setCustomDate] = useState('');
    const [note, setNote] = useState(initialNote ?? '');
    const [dateError, setDateError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    // Catches a second click that lands before the disabled button renders.
    const submittingRef = useRef(false);

    const selectDuration = (value: SnoozeDuration) => {
        setDuration(value);
        setDateError(null);
    };

    const handleSave = async () => {
        if (submittingRef.current) return;
        if (duration === 'Custom') {
            if (!customDate) { setDateError(t(TranslationKey.Home.IgnoreModal.CustomDateRequired)); return; }
            if (!isFutureDate(customDate)) { setDateError(t(TranslationKey.Home.IgnoreModal.CustomDateNotInFuture)); return; }
        }
        const trimmedNote = note.trim();
        submittingRef.current = true;
        setSubmitting(true);
        try {
            await onConfirm({ snoozeDuration: duration, customDate: duration === 'Custom' ? customDate : null, note: trimmedNote || null });
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    const radioName = `ignore-relance-duration-${taskId}`;
    const durationTestId = (value: SnoozeDuration) => `ignore-relance-duration-${value.toLowerCase()}-${taskId}`;

    return (
        <ModalShell
            heading={t(TranslationKey.Home.IgnoreModal.Heading)}
            onClose={onCancel}
            dismissAriaLabel={t(TranslationKey.Home.IgnoreModal.Cancel)}
            rootTestId={`ignore-relance-modal-${taskId}`}
            panelClassName="bg-surface-elevated ring-1 ring-line-subtle rounded-lg shadow-strong w-full max-w-lg mx-l p-xl flex flex-col gap-l"
            footer={
                <>
                    <Button type="button" disabled={submitting} onClick={() => void handleSave()} data-testid={`ignore-relance-save-${taskId}`}>
                        {t(TranslationKey.Home.IgnoreModal.Save)}
                    </Button>
                    <Button variant="secondary" type="button" onClick={onCancel} data-testid={`ignore-relance-cancel-${taskId}`}>
                        {t(TranslationKey.Home.IgnoreModal.Cancel)}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col">
                <p className="-mt-m text-s text-content-secondary mb-l">
                    {customerName}{formattedAmount ? ` — ${formattedAmount}` : ''}
                </p>

                <p className="text-s font-semibold text-content mb-m" data-testid={`ignore-relance-section-snooze-${taskId}`}>
                    {t(TranslationKey.Home.IgnoreModal.SectionSnooze)}
                </p>
                <div className="flex flex-col gap-s mb-l">
                    <Radio name={radioName} value="Hours24" label={t(TranslationKey.Home.Snooze.Hours24)}
                        checked={duration === 'Hours24'} onChange={() => selectDuration('Hours24')}
                        data-testid={durationTestId('Hours24')} />
                    <Radio name={radioName} value="Days7" label={t(TranslationKey.Home.Snooze.Days7)}
                        checked={duration === 'Days7'} onChange={() => selectDuration('Days7')}
                        data-testid={durationTestId('Days7')} />
                    <Radio name={radioName} value="Days14" label={t(TranslationKey.Home.Snooze.Days14)}
                        checked={duration === 'Days14'} onChange={() => selectDuration('Days14')}
                        data-testid={durationTestId('Days14')} />
                    <div className="flex items-center gap-s min-w-0">
                        <Radio name={radioName} value="Custom" aria-label={t(TranslationKey.Home.Snooze.Custom)}
                            checked={duration === 'Custom'} onChange={() => selectDuration('Custom')}
                            data-testid={durationTestId('Custom')} className="shrink-0" />
                        <DateInput
                            compact
                            value={customDate}
                            onChange={(value) => { setCustomDate(value); setDateError(null); setDuration('Custom'); }}
                            data-testid={`ignore-relance-custom-date-${taskId}`}
                            className="flex-1 min-w-0"
                        />
                    </div>
                    <ValidationMessage message={dateError ?? undefined} data-testid={`ignore-relance-date-error-${taskId}`} />
                </div>

                <hr className="border-line-subtle mb-l" />

                <p className="text-s font-semibold text-content mb-m" data-testid={`ignore-relance-section-ignore-${taskId}`}>
                    {t(TranslationKey.Home.IgnoreModal.SectionIgnore)}
                </p>
                <div className="mb-l">
                    <Radio name={radioName} value="Indefinite" label={t(TranslationKey.Home.IgnoreModal.IndefiniteLabel)}
                        checked={duration === 'Indefinite'} onChange={() => selectDuration('Indefinite')}
                        data-testid={durationTestId('Indefinite')} />
                </div>

                <hr className="border-line-subtle mb-l" />

                <TextArea
                    id={`ignore-relance-note-${taskId}`}
                    label={t(TranslationKey.Home.IgnoreModal.NoteLabel)}
                    placeholder={t(TranslationKey.Home.IgnoreModal.NotePlaceholder)}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    data-testid={`ignore-relance-note-input-${taskId}`}
                />
            </div>
        </ModalShell>
    );
};
